/**
 * MypageOrderDetailsContainer
 * --
 */
import React from 'react';
import MypageOrderDetailsPresenter from './MypageOrderDetailsPresenter';
import { useUserDataAction, useUserDataStack, useUserStack } from 'store';
import { useParams } from 'react-router-dom';
import { decrypt } from 'utils/Crypto';
import MessageAlert, {
  PAYMENT_STORE_ID,
  getPaymentDueDate,
  DOMAIN_URL,
  DEPLOY_TYPE,
  calculateTotalOptionPrice,
  POINT_STORE_ID,
} from 'utils';
import {
  useCreateRefundDelivery,
  useCreateRefundSubscription,
  useCreateRefundRental,
} from 'hooks/RefundHooks';
import {
  useBulkUpdateParcelService,
  useConfirmParcelServiceStatus,
} from 'hooks/ParcelServiceHooks';
import {
  usePaymentCancelAccount,
  usePaymentCancelCard,
  usePaymentCancelMobile,
} from 'hooks/PaymentHooks.';
import { useMyShopOrderList } from 'hooks/custom';
import { useBulkUpdateRental } from 'hooks/RentalHooks';

const MypageOrderDetailsContainer = (props) => {
  /* ===== ROUTE ===== */
  const { orderId } = useParams();

  // 주문 idx
  const orderIdx = React.useMemo(() => {
    const decodeIdx = decrypt(orderId);

    return Number(decodeIdx);
  }, [orderId]);

  /* ===== PROPS ===== */
  const { navigate, screenSize } = props;

  /* ===== Mutate ===== */
  const { confirmParcelServiceStatus } = useConfirmParcelServiceStatus();
  const { bulkUpdateParcelService } = useBulkUpdateParcelService();
  const { bulkUpdateRental } = useBulkUpdateRental();
  const { createRefundDelivery } = useCreateRefundDelivery();
  const { createRefundSubscription } = useCreateRefundSubscription();
  const { cancelCard } = usePaymentCancelCard();
  const { cancelAccount } = usePaymentCancelAccount();
  const { cancelMobile } = usePaymentCancelMobile();
  const { createRefundRental } = useCreateRefundRental();

  /* ===== Store ===== */
  const userInfo = useUserStack();
  const orderList = useMyShopOrderList(userInfo.user_id);
  const userDataAction = useUserDataAction();

  const { cardList, orderList: paymentList } = useUserDataStack();

  /* ===== STATE ===== */
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);

  const [selectedParcel, setSelectedParcel] = React.useState([]);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 구매확정, 취소/환불
   * --
   * @param {string} type
   * @param {object} data
   * @returns
   */
  const handleOrderStatus = async (type, data) => {
    const submitType = type.toLocaleUpperCase();

    try {
      const confirm = (msg) => {
        return window.confirm(msg);
      };

      switch (submitType) {
        // SECTION: 구매확정
        case 'DELIVERY-CONFIRMED':
          if (
            !confirm(
              '상품 주문을 확정하시겠습니까?\n주문 확정 후 취소/환불은 불가능합니다.',
            )
          )
            return;

          /**
           * STUB data 형식
           * --
           *  {
           *    shop_order_id: number
           *
           *    parcel_service_id: number
           *    purchase_status: string
           *  }
           */
          const { status: confirmedStatus } =
            await confirmParcelServiceStatus(data);

          if (confirmedStatus !== 200) {
            MessageAlert.warning('주문 확정에 실패하였습니다!');
            return;
          }

          const newOrderList = orderList.map((order) =>
            order.shop_order_id !== data.shop_order_id
              ? order
              : {
                  ...order,
                  parcel_service: order.parcel_service.map((parcel) =>
                    parcel.parcel_service_id !== data.parcel_service_id
                      ? parcel
                      : {
                          ...parcel,
                          purchase_status: 'CONFIRMED',
                        },
                  ),
                },
          );
          userDataAction.setData('orderList', newOrderList);

          MessageAlert.success('상품 주문이 확정되었습니다.');
          return;
        // SECTION: 주문취소
        case 'DELIVERY-CANCELLED':
          if (!data.refund_reason)
            return MessageAlert.info('환불/취소 사유를 선택해주세요');

          /**
           * STUB data 형식
           * --
           *  {
           *    //NOTE - 결제 취소
           *    shop_order_id: number
           *    payment_info: object
           *    payment_method: string
           *
           *    //NOTE - 환불 데이터
           *    parcel_service_id: number
           *    refund_reason: string
           *    refund_amount: number
           *    return_num: number
           *    tax_free: number
           *  }
           */
          if (!confirm('상품 주문을 취소하시겠습니까?')) return;

          setIsLoading(true);

          const {
            shop_order_id,
            payment_method,
            payment_info,
            parcel_service_id,
            refund_reason,
            refund_amount,
            refund_num,
            tax_free,
          } = data;

          let cancelRes = null;

          // 해당 주문건 찾기
          const shopOrder = orderList.find(
            (order) => order.shop_order_id === data.shop_order_id,
          );

          const parcel = shopOrder?.parcel_service?.find(
            (par) => par?.parcel_service_id === parcel_service_id,
          );

          // 간편결제건인지 확인
          const isQuickPay = parcel?.card_id ? true : false;

          // 취소 sequence 번호 계산
          const sequence = shopOrder?.parcel_service.reduce((acc, parcel) => {
            const { refund } = parcel;

            if (refund) {
              ++acc;
            }

            return acc;
          }, 1);

          // 마지막 환불건인지 체크
          // 마지막 배송건이면 배송비 리턴
          const isLast =
            shopOrder.parcel_service.length === sequence
              ? (shopOrder.final_delivery_price ?? 0)
              : 0;

          // NOTE: 결제취소
          if (payment_info && payment_method !== 'JM_PAY') {
            const cancelData = {
              mid: isQuickPay ? POINT_STORE_ID : PAYMENT_STORE_ID,
              cancelType: 'PARTIAL',
              orgTradeKeyType: 'TID',
              orgTradeKey: `${
                payment_info.trno ? payment_info.trno : payment_info.tid
              }`,
              cancelTotalAmount: `${refund_amount + isLast}`,
              cancelTaxFreeAmount: `${tax_free ?? 0}`,
              cancelSeq: `${sequence}`,
            };

            // SECTION: 카드취소
            if (
              payment_method === 'CARD' ||
              payment_method === 'NAVER_PAY' ||
              payment_method === 'KAKAO_PAY'
            ) {
              cancelRes = await cancelCard(cancelData);
            }
            // SECTION: 휴대폰결제
            else if (payment_method === 'MOBILE') {
              cancelRes = await cancelMobile(cancelData);
            }
            // SECTION: 계좌이체
            else if (
              payment_method === 'TRANSFER' ||
              payment_method === 'VIRTUAL_ACCOUNT'
            ) {
              cancelRes = await cancelAccount(cancelData);
            }

            // NOTE: 결제 취소 실패 시 환불 x
            if (cancelRes?.data?.code !== 'A0200') {
              console.log(payment_method);
              MessageAlert.warning('결제 취소에 실패하였습니다!');
              return;
            }
          }

          // NOTE: 주문취소
          const { status: cancelStatus, data: cancelData } =
            await createRefundDelivery({
              parcel_service_id,
              refund_reason,
              refund_amount: refund_amount + isLast,
              refund_num,
              refund_payment_info: cancelRes ? cancelRes?.data?.data : null,
            });

          // NOTE: 주문취소 실패 처리
          if (cancelStatus !== 200) {
            MessageAlert.warning('주문 취소에 실패하였습니다!');
            return;
          }

          const newShopOrderList = orderList.map((order) =>
            order.shop_order_id !== shop_order_id
              ? order
              : {
                  ...order,
                  parcel_service: order.parcel_service.map((parcel) =>
                    parcel.parcel_service_id !== parcel_service_id
                      ? parcel
                      : {
                          ...parcel,
                          purchase_status: 'REFUNDED',
                          refund: cancelData,
                        },
                  ),
                },
          );

          userDataAction.setData('orderList', newShopOrderList);

          MessageAlert.success('상품 주문이 취소되었습니다!');
          return cancelStatus;
        // SECTION: 환불요청
        case 'DELIVERY-REFUNDED':
          if (!data.refund_reason)
            return MessageAlert.info('환불/취소 사유를 선택해주세요');

          /**
           * STUB data 형식
           * --
           *  {
           *    //NOTE -
           *    shop_order_id: number
           *
           *    //NOTE - 환불 데이터
           *    parcel_service_id: number
           *    refund_reason: string
           *    refund_amount: number
           *    return_num: number
           *  }
           */
          if (!confirm('환불 요청하시겠습니까?')) return;

          setIsLoading(true);

          // NOTE: 환불
          const { status: refundStatus, data: refundData } =
            data.type === 'SUBSCRIPTION'
              ? await createRefundSubscription({
                  parcel_service_id: data.parcel_service_id,
                  refund_reason: data.refund_reason,
                  // refund_amount: data.refund_amount,
                  return_num: data.return_num,
                })
              : await createRefundDelivery({
                  parcel_service_id: data.parcel_service_id,
                  refund_reason: data.refund_reason,
                  // refund_amount: data.refund_amount,
                  return_num: data.return_num,
                });

          if (refundStatus !== 200) {
            MessageAlert.warning('주문 취소에 실패하였습니다!');
            return;
          }

          const newShopList = orderList.map((order) =>
            order.shop_order_id !== data.shop_order_id
              ? order
              : {
                  ...order,
                  parcel_service: order.parcel_service.map((parcel) =>
                    parcel.parcel_service_id !== data.parcel_service_id
                      ? parcel
                      : {
                          ...parcel,
                          purchase_status: 'REFUNDED',
                          refund: refundData,
                        },
                  ),
                },
          );

          userDataAction.setData('orderList', newShopList);

          MessageAlert.success('환불이 성공적으로 접수되었습니다!');
          return refundStatus;
        // SECTION: 정기배송 구매확정
        case 'SUBSCRIPTION-CONFIRMED':
          if (
            !confirm(
              '상품 주문을 확정하시겠습니까?\n주문 확정 후 취소/환불은 불가능합니다.',
            )
          )
            return;

          /**
           * STUB data 형식
           * --
           *  {
           *    shop_order_id: number
           *
           *    parcel_service_id: number
           *    purchase_status: string
           *    regular_payment_id: number
           *    regular_payment_info: object
           *  }
           */
          const newConfirmData = data.regular_payment_info.map((info) =>
            info.regular_payment_id !== data.regular_payment_id
              ? info
              : {
                  ...info,
                  purchase_status: data.purchase_status,
                },
          );
          const { status: regularConfirmedStatus } =
            await confirmParcelServiceStatus({
              parcel_service_id: data.parcel_service_id,
              regular_payment_id: data.regular_payment_id,
            });

          if (regularConfirmedStatus !== 200) {
            MessageAlert.warning('주문 확정에 실패하였습니다!');
            return;
          }

          const newRegularOrderList = orderList.map((order) =>
            order.shop_order_id !== data.shop_order_id
              ? order
              : {
                  ...order,
                  parcel_service: order.parcel_service.map((parcel) =>
                    parcel.parcel_service_id !== data.parcel_service_id
                      ? parcel
                      : {
                          ...parcel,
                          regular_payment_info: newConfirmData,
                        },
                  ),
                },
          );
          userDataAction.setData('orderList', newRegularOrderList);

          MessageAlert.success('상품 주문이 확정되었습니다.');
          return;
        // SECTION: 정기배송 주문취소
        case 'SUBSCRIPTION-CANCELLED': {
          if (!data.refund_reason)
            return MessageAlert.info('환불/취소 사유를 선택해주세요');

          /**
           * STUB data 형식
           * --
           *  {
           *    //NOTE - 결제 취소
           *    shop_order_id: number
           *    regular_payment_info: object
           *
           *    //NOTE - 환불 데이터
           *    parcel_service_id: number
           *    refund_reason: string
           *    refund_amount: number
           *    return_num: number
           *    tax_rate: number
           *    isFirst: boolean
           *  }
           */
          if (!confirm('상품 주문을 취소하시겠습니까?')) return;

          setIsLoading(true);

          let regularCancelRes = null;
          let cancelAmount = 0;

          // 1회차 배송일 경우
          if (data.isFirst) {
            // 해당 주문건 찾기
            const shopOrder = orderList.find(
              (order) => order.shop_order_id === data.shop_order_id,
            );

            // 취소 sequence 번호 계산
            const sequence = shopOrder?.parcel_service.reduce((acc, parcel) => {
              const { refund } = parcel;

              if (refund) {
                ++acc;
              }

              return acc;
            }, 1);

            // 마지막 환불건인지 체크
            // 마지막 배송건이면 배송비 리턴
            const isLast =
              shopOrder.parcel_service.length === sequence
                ? (shopOrder.final_delivery_price ?? 0)
                : 0;

            // NOTE: 결제취소
            cancelAmount = data.regular_payment_info?.price + isLast;
            const cancelData = {
              mid: POINT_STORE_ID,
              cancelType: 'PARTIAL',
              orgTradeKeyType: 'TID',
              orgTradeKey: `${data.regular_payment_info.payment_info?.tid}`,
              cancelTotalAmount: `${cancelAmount}`,
              cancelTaxFreeAmount: `${data.tax_free ?? 0}`,
              cancelSeq: `${sequence}`,
            };

            // SECTION: 카드취소
            regularCancelRes = await cancelCard(cancelData);

            // NOTE: 결제 취소 실패 시
            if (
              regularCancelRes.status !== 200 ||
              regularCancelRes.data.code !== 'A0200'
            ) {
              MessageAlert.warning('주문 취소에 실패하였습니다!');
              return;
            }
          }
          // 2회차 이상일 경우 주문 취소
          else {
            // 정기배송이 결제되었을 경우 결제 취소
            if (data.regular_payment_info.payment_info) {
              const regularCancel = {
                mid: POINT_STORE_ID,
                cancelType: 'FULL',
                orgTradeKeyType: 'TID',
                orgTradeKey: data.regular_payment_info.payment_info?.tid,
              };

              cancelAmount = data.regular_payment_info?.price;
              regularCancelRes = await cancelCard(regularCancel);

              // NOTE: 결제 취소 실패 시
              if (
                regularCancelRes.status !== 200 ||
                regularCancelRes.data.code !== 'A0200'
              ) {
                MessageAlert.warning('주문 취소에 실패하였습니다!');
                return;
              }
            }
          }

          const cancelRegular = await createRefundSubscription({
            parcel_service_id: data.parcel_service_id,
            refund_reason: data.refund_reason,
            refund_amount: cancelAmount,
            refund_num: data.regular_payment_info.num,
            refund_payment_info: regularCancelRes.data.data,
            regular_payment_id: data.regular_payment_info.regular_payment_id,
          });

          if (cancelRegular.status !== 200) {
            MessageAlert.warning('주문 취소에 실패하였습니다!');
            return;
          }

          const newParcelCancelData = orderList.map((order) =>
            order.shop_order_id !== data.shop_order_id
              ? order
              : {
                  ...order,
                  payment_state: 'PARTIALLY_REFUNDED',
                  parcel_service: order.parcel_service.map((parcel) =>
                    parcel.parcel_service_id !== data.parcel_service_id
                      ? parcel
                      : {
                          ...parcel,
                          purchase_status: 'REFUNDED',
                          regular_payment_info: parcel.regular_payment_info.map(
                            (regular) =>
                              regular.regular_payment_id !==
                              data.regular_payment_info.regular_payment_id
                                ? regular
                                : {
                                    ...regular,
                                    payment_state: 'REFUNDED',
                                    purchase_status: 'REFUNDED',
                                    delivery_status: 'RETURN_COMPLETED',
                                  },
                          ),
                          refund: cancelRegular.data,
                        },
                  ),
                },
          );

          userDataAction.setData('orderList', newParcelCancelData);

          MessageAlert.success('상품 주문이 취소되었습니다!');
          return cancelRegular.status;
        }
        // SECTION: 정기배송 환불
        case 'SUBSCRIPTION-REFUNDED': {
          if (!data.refund_reason)
            return MessageAlert.info('환불/취소 사유를 선택해주세요');

          /**
           * STUB data 형식
           * --
           *  {
           *    //NOTE - 결제 취소
           *    shop_order_id: number
           *    parcel_service_id: number
           *    regular_payment_info: object
           *    refund_reason: string
           *  }
           */
          if (!confirm('상품 환불/반품 요청하시겠습니까?')) return;

          setIsLoading(true);

          const cancelRegular = await createRefundSubscription({
            parcel_service_id: data.parcel_service_id,
            refund_reason: data.refund_reason,
            refund_amount: data.regular_payment_info.price,
            refund_num: data.regular_payment_info.num,
            refund_payment_info: null,
            regular_payment_id: data.regular_payment_info.regular_payment_id,
          });

          if (cancelRegular.status !== 200) {
            MessageAlert.warning('환불/반품 신청에 실패하였습니다!');
            return;
          }

          const newParcelCancelData = orderList.map((order) =>
            order.shop_order_id !== data.shop_order_id
              ? order
              : {
                  ...order,
                  parcel_service: order.parcel_service.map((parcel) =>
                    parcel.parcel_service_id !== data.parcel_service_id
                      ? parcel
                      : {
                          ...parcel,
                          purchase_status: 'REFUNDED_REQUESTED',
                          regular_payment_info: parcel.regular_payment_info.map(
                            (regular) =>
                              regular.regular_payment_id !==
                              data.regular_payment_info.regular_payment_id
                                ? regular
                                : {
                                    ...regular,
                                    payment_state: 'REFUNDED',
                                    purchase_status: 'REFUNDED',
                                    delivery_status: 'REFUNDED_REQUESTED',
                                  },
                          ),
                          refund: cancelRegular.data,
                        },
                  ),
                },
          );

          userDataAction.setData('orderList', newParcelCancelData);

          MessageAlert.success('상품 주문이 취소되었습니다!');
          return cancelRegular.status;
        }
        case 'RENTAL-CANCELLED':
          if (!confirm('상품 주문을 취소하시겠습니까?')) return;

          setIsLoading(true);

          let rentalCancelRes = null;
          // 렌탈 결제 취소
          const rentalCancel = {
            mid: POINT_STORE_ID,
            cancelType: 'FULL',
            orgTradeKeyType: 'TID',
            orgTradeKey: data?.rental_payment_info?.[0]?.payment_info?.tid,
          };

          rentalCancelRes = await cancelCard(rentalCancel);

          // NOTE: 결제 취소 실패 시
          if (
            rentalCancelRes.status !== 200 ||
            rentalCancelRes.data.code !== 'A0200'
          ) {
            MessageAlert.warning('주문 취소에 실패하였습니다!');
            return;
          }

          const cancelRental = await createRefundRental({
            rental_id: data.rental_id,
            refund_reason: data.refund_reason,
            rental_payment_id: data.rental_payment_id,
            refund_payment_info: rentalCancelRes.data.data,
          });

          if (cancelRental.status !== 200) {
            MessageAlert.warning('주문 취소에 실패하였습니다!');
            return;
          }

          const newRentalCancelData = orderList.map((order) =>
            order.shop_order_id !== data.shop_order_id
              ? order
              : {
                  ...order,
                  payment_state: 'PARTIALLY_REFUNDED',
                  rental: order.rental.map((rental) =>
                    rental.rental_id !== data.rental_id
                      ? rental
                      : {
                          ...rental,
                          purchase_status: 'REFUNDED',
                          rental_payment_info: rental.rental_payment_info.map(
                            (info) =>
                              info.rental_payment_id !== data.rental_payment_id
                                ? info
                                : {
                                    ...info,
                                    payment_state: 'REFUNDED',
                                    purchase_status: 'REFUNDED',
                                  },
                          ),
                          refund: cancelRental.data,
                        },
                  ),
                },
          );

          userDataAction.setData('orderList', newRentalCancelData);
          MessageAlert.success('상품 주문이 취소되었습니다!');
          setTimeout(() => window.location.reload(), 500);
          return rentalCancel.status;
        case 'RENTAL-REFUNDED':
          if (
            !confirm(
              '계약 해지 요청을 하시겠습니까?\n계약 해지 시 위약금이 발생할 수 있습니다.',
            )
          )
            return;

          const refundRental = await createRefundRental({
            rental_id: data.rental_id,
            refund_reason: data.refund_reason,
            rental_payment_id: data.rental_payment_id,
          });

          if (refundRental.status !== 200) {
            MessageAlert.warning('주문 취소에 실패하였습니다!');
            return;
          }

          const newRentalRefundData = orderList.map((order) =>
            order.shop_order_id !== data.shop_order_id
              ? order
              : {
                  ...order,
                  payment_state: 'PARTIALLY_REFUNDED',
                  rental: order.rental.map((rental) =>
                    rental.rental_id !== data.rental_id
                      ? rental
                      : {
                          ...rental,
                          purchase_status: 'REFUNDED',
                          rental_payment_info: rental.rental_payment_info.map(
                            (info) =>
                              info.rental_payment_id !== data.rental_payment_id
                                ? info
                                : {
                                    ...info,
                                    payment_state: 'REFUNDED',
                                    purchase_status: 'REFUNDED',
                                  },
                          ),
                          refund: refundRental.data,
                        },
                  ),
                },
          );

          userDataAction.setData('orderList', newRentalRefundData);
          MessageAlert.success('계약 해지 신청되었습니다!');
          setTimeout(() => window.location.reload(), 500);
          return refundRental.status;
        default:
          return MessageAlert.info('주문 상태 변경 실패!');
      }
    } catch (err) {
      console.error('[handleOrderStatus] Error!! : ', err);
      MessageAlert.error(
        '상품 취소/환불에 실패하였습니다! 관리자에게 문의해주세요!',
      );
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  /**
   * 상품 선택
   * --
   * @param {object} d
   */
  const handleCheckedList = (d) => {
    const keyItem = selectedParcel;

    if (keyItem) {
      const isExist = keyItem.findIndex(
        (item) => item.parcel_service_id === d.parcel_service_id,
      );

      if (isExist === -1) {
        setSelectedParcel((prev) => [...prev, d]);
      } else {
        setSelectedParcel((prev) => [
          ...prev.slice(0, isExist),
          ...prev.slice(isExist + 1),
        ]);
      }
    } else {
      setSelectedParcel((prev) => [...prev, d]);
    }
  };

  /**
   * 상품 전체 선택
   * --
   * @param {array} list
   */
  const handleCheckedListAll = (list) => {
    const isCheckedAll = list.length === selectedParcel.length;

    if (isCheckedAll) {
      setSelectedParcel([]);
    } else {
      setSelectedParcel(list);
    }
  };

  /**
   * 정기배송 미루기/취소
   * --
   * @param {string} type
   * @returns
   */
  const handleRegularStatus = async (type) => {
    if (!selectedParcel.length) {
      MessageAlert.info(
        `${type === 'DELAY' ? '미루기' : '구독취소'}할 상품을 선택해주세요`,
      );
      return false;
    }

    try {
      setIsLoading(true);

      let res = null;
      let updateParcel = null;

      const submitType = type.toLocaleUpperCase();
      switch (submitType) {
        case 'DELAY':
          updateParcel = selectedParcel.map((parcel) => {
            const { regular_cycle } = parcel;
            const [num, cycle, delay] = regular_cycle.split('-');

            return {
              parcel_service_id: parcel.parcel_service_id,
              is_regular_postponed: 'Y',
              regular_updated_at: getPaymentDueDate(
                parcel.regular_updated_at,
                regular_cycle,
              ), // 결제 예정일 업데이트
              regular_cycle: `${num}-${cycle}-${Number(delay) + 1}`, // 미루기 횟수 업데이트
            };
          });

          res = await bulkUpdateParcelService(updateParcel);
          break;
        case 'CANCEL':
          updateParcel = selectedParcel.map((parcel) => ({
            parcel_service_id: parcel.parcel_service_id,
            is_active: 'N',
          }));

          res = await bulkUpdateParcelService(updateParcel);
          break;
        default:
          MessageAlert.info(
            `정기배송 ${
              submitType === 'DELAY' ? '미루기' : '구독취소'
            }에 실패하였습니다!`,
          );
          return false;
      }

      if (res.status !== 200) {
        MessageAlert.info(
          `정기배송 ${
            submitType === 'DELAY' ? '미루기' : '구독취소'
          }에 실패하였습니다!`,
        );
        return false;
      }

      MessageAlert.success(
        `정기배송 ${
          submitType === 'DELAY' ? '미루기' : '구독취소'
        }가 신청되었습니다.`,
      );

      navigate('/mypage/order?type=subscription');
      return true;
    } catch (err) {
      console.error('[handleRegularStatus] Error!! ', err);
      MessageAlert.error(
        `정기배송 ${type === 'DELAY' ? '미루기' : '구독취소'}에 실패하였습니다!`,
      );
      return false;
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  /**
   * 결제 카드 변경
   * --
   * @param {object} data { card_id: number, type: string }
   * @returns
   */

  const handleChangePaymentCard = async (data = null) => {
    if (!data) return;

    const confirm = window.confirm('결제 카드를 변경하시겠습니까?');

    if (!confirm) return;

    try {
      setIsLoading(true);

      const { card_id, type } = data;

      let updateList = [],
        key = '',
        mutate;
      switch (type) {
        case 'SUBSCRIPTION':
          updateList = OrderData.parcel_service.map((parcel) => ({
            parcel_service_id: parcel.parcel_service_id,
            card_id: card_id,
          }));
          key = 'parcel_service';
          mutate = bulkUpdateParcelService;
          break;
        case 'RENTAL':
          updateList = OrderData.rental.map((rent) => ({
            rental_id: rent.rental_id,
            card_id: card_id,
          }));
          key = 'rental';
          mutate = bulkUpdateRental;
          break;
        default:
          return false;
      }

      const { status } = await mutate(updateList);

      if (status !== 200) {
        MessageAlert.info('결제카드 변경에 실패하였습니다.');
        return false;
      }

      const newOrderList = paymentList.map((shopOrder) =>
        shopOrder.shop_order_id !== orderIdx
          ? shopOrder
          : {
              ...shopOrder,
              [key]: shopOrder[key].map((item) => ({
                ...item,
                card_id: card_id,
              })),
            },
      );

      userDataAction.setData('orderList', newOrderList);
      MessageAlert.success('변경되었습니다.');
      return true;
    } catch (err) {
      console.error('[handleChangePaymentCard] Error!! : ', err);
      MessageAlert.error('결제카드 변경에 실패하였습니다.');
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 주문 데이터 정제
   * --
   */
  const OrderData = React.useMemo(() => {
    if (!orderList.length || !orderIdx) return;

    const mapOrderData = new Map(
      orderList.map((order) => [order.shop_order_id, order]),
    );
    const orderData = mapOrderData.get(orderIdx);

    // SECTION: 필요 데이터 정제
    const newOrderData = {
      ...orderData,
      total_product_price: orderData.product_information.reduce(
        (acc, cur) =>
          (acc +=
            (cur.price + calculateTotalOptionPrice(cur.product_option)) *
            cur.num),
        0,
      ),
      ...orderData.product_information.reduce(
        (acc, cur) => {
          acc['coupon_discount_price'] +=
            (cur.coupon_discount_price ?? 0) * cur.num;
          acc['promotion_discount_price'] +=
            (cur.promotion_discount_price ?? 0) * cur.num;
          acc['grade_discount_price'] +=
            (cur.grade_discount_price ?? 0) * cur.num;

          return acc;
        },
        {
          grade_discount_price: 0,
          coupon_discount_price: 0,
          promotion_discount_price: 0,
        },
      ),
    };

    // SECTION: 같은 운송장 번호끼리 그룹화
    const { parcel_service, ...rest } = newOrderData;

    const newParcelList = parcel_service.reduce((acc, cur) => {
      const { invoice_number } = cur;

      if (acc[invoice_number]) {
        acc[invoice_number].push(cur);
      } else {
        acc[invoice_number] = [cur];
      }

      return acc;
    }, {});

    return {
      ...newOrderData,
      shop_order: Object.entries(newParcelList).map(([key, value]) => ({
        ...rest,
        invoice_number: key,
        delivery_company: value[0]?.delivery_company,
        parcel_service: value,
      })),
    };
  }, [orderList, orderIdx]);

  /**
   * 카드 데이터 정제
   * --
   */
  const CardData = React.useMemo(() => {
    if (!cardList.length) return { SUBSCRIPTION: [], RENTAL: [] };

    // 렌탈, 정기배송 카드 분류
    const ClassificationCard = cardList.reduce(
      (acc, card) => {
        const { type } = card;

        if (type === 'SUBSCRIPTION' || type === 'RENTAL') {
          acc[type].push(card);
        }

        return acc;
      },
      { SUBSCRIPTION: [], RENTAL: [] },
    );

    return ClassificationCard;
  }, [cardList]);

  /**
   * 팝업 메세지 감지
   * --
   */
  React.useEffect(() => {
    // 메시지 수신 핸들러
    const handleMessage = (event) => {
      const allowedOrigins = [
        `http://${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `http://mall.${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://mall.${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://jm-coffee.${DOMAIN_URL}${
          DEPLOY_TYPE === 'local' ? ':3000' : ''
        }`,
        `https://mall-jm-coffee.${DOMAIN_URL}${
          DEPLOY_TYPE === 'local' ? ':3000' : ''
        }`,
      ];

      // 보안 체크: 같은 origin에서 온 메시지만 허용
      if (!allowedOrigins.includes(event.origin)) {
        console.warn('허용되지 않은 origin:', event.origin);
        return;
      }

      const message = event.data;

      // 팝업에서 보낸 데이터만 처리
      if (message?.source === 'card-register') {
        const parseData = JSON.parse(message?.data);

        userDataAction.addData('cardList', parseData);
      }
    };

    window.addEventListener('message', handleMessage);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line
  }, []);

  /* ===== RENDER ===== */
  return (
    <>
      <MypageOrderDetailsPresenter
        order={OrderData}
        navigate={navigate}
        isLoading={isLoading}
        selectedParcel={selectedParcel}
        onOrderStatus={handleOrderStatus}
        onRegularStatus={handleRegularStatus}
        onCheckedList={handleCheckedList}
        onCheckedListAll={handleCheckedListAll}
        screenSize={screenSize}
        card={CardData}
        onChangePaymentCard={handleChangePaymentCard}
        userInfo={userInfo}
      />
    </>
  );
};

export default MypageOrderDetailsContainer;
